import type { OrgRole } from '@/api/types'

/**
 * Who may do what.
 *
 * A role is data; a permission check is code. Nothing in the console asks
 * `role === 'admin'`, because that question has to be re-answered correctly in
 * every place it is asked and one of them will be wrong. It asks
 * `can(role, 'knowledge.publish')`, and the answer lives here, once.
 *
 * The obvious implementation was tried and rejected. The spec lists the six
 * roles in order and says each includes everything below it, which invites a
 * rank comparison: give every action a required rank and check
 * `RANK[role] >= RANK[action]`. Two lines, no table, and wrong.
 *
 * It is wrong because `field_agent` is not a junior `manager`. The spec is
 * explicit that it is a narrower, mostly write-only role: an agent owns the
 * tasks and cases assigned to them, enrols people and files reports, and
 * cannot browse the community. A rank comparison cannot express that. Placing
 * `field_agent` above `viewer` hands it the entire aggregate insight surface it
 * was never meant to have; placing it below hands `viewer` the ability to file
 * a report. Either way the mistake is silent, because a numeric comparison has
 * no place to write down that the ordering has an exception in it.
 *
 * So the table is explicit, one line per action, listing the roles that hold
 * it. It reads as "who can do this", which is the question asked in review, and
 * a new action that somebody forgets to add is denied to everybody rather than
 * granted to half the organization.
 *
 * This is a convenience, not a control. The server checks the same table on
 * every request and is the only thing standing between a role and an action;
 * everything here does is decide whether to render a door that would not open.
 * A console that hides nothing is confusing, and a console that relies on
 * hiding is unprotected.
 */

export type Permission =
  /* The organization itself. */
  | 'org.read'
  | 'org.update'
  | 'org.delete'
  | 'billing.manage'
  /* Staff. */
  | 'staff.read'
  | 'staff.invite'
  | 'staff.update'
  | 'staff.remove'
  /* Projects. */
  | 'projects.read'
  | 'projects.create'
  | 'projects.update'
  | 'projects.close'
  /* Knowledge. */
  | 'knowledge.read'
  | 'knowledge.write'
  | 'knowledge.publish'
  /* Enrolment. */
  | 'codes.read'
  | 'codes.write'
  | 'members.read'
  | 'members.enrol'
  /* Conversations, split by what they expose. */
  | 'conversations.readAggregate'
  | 'conversations.readIndividual'
  /* Insight. */
  | 'insights.read'
  | 'insights.review'
  /* Cases. */
  | 'cases.read'
  | 'cases.readAssigned'
  | 'cases.write'
  | 'cases.assign'
  | 'cases.refer'
  /* Broadcasts. */
  | 'broadcasts.read'
  | 'broadcasts.write'
  | 'broadcasts.send'
  /* Field work. */
  | 'tasks.read'
  | 'tasks.readAssigned'
  | 'tasks.write'
  | 'tasks.report'
  | 'tasks.review'
  /* Measurement, audit, export. */
  | 'indicators.read'
  | 'indicators.write'
  | 'audit.read'
  | 'reports.export'

const ALL_ROLES: readonly OrgRole[] = [
  'owner',
  'admin',
  'manager',
  'analyst',
  'field_agent',
  'viewer',
]

/**
 * The table. One line per action, in the order the `Permission` union declares
 * them, so a missing entry is visible as a gap rather than hidden in a sort.
 */
const GRANTS: Record<Permission, readonly OrgRole[]> = {
  /*
   * Everybody can read the organization they work for; its name is on every
   * screen. Only the owner can bill it or end it, which is the one place the
   * spec draws a line above `admin`.
   */
  'org.read': ALL_ROLES,
  'org.update': ['owner', 'admin'],
  'org.delete': ['owner'],
  'billing.manage': ['owner'],

  /*
   * A manager can see who is on the team, because assigning a case to somebody
   * requires knowing they exist, and cannot change any of it. `analyst` reads
   * everything by definition. `field_agent` and `viewer` have no business with
   * a staff roster.
   */
  'staff.read': ['owner', 'admin', 'manager', 'analyst'],
  'staff.invite': ['owner', 'admin'],
  'staff.update': ['owner', 'admin'],
  'staff.remove': ['owner', 'admin'],

  /*
   * Creating and closing a project is an admin act; running one is the
   * manager's job, and editing the project's own settings is part of running
   * it. `field_agent` is absent on purpose: an agent's task carries the project
   * it belongs to, and that is all they need to see.
   */
  'projects.read': ['owner', 'admin', 'manager', 'analyst', 'viewer'],
  'projects.create': ['owner', 'admin'],
  'projects.update': ['owner', 'admin', 'manager'],
  'projects.close': ['owner', 'admin'],

  /*
   * A viewer can read published guidance. It is not an individual record and it
   * is the thing being said to their community in their name.
   */
  'knowledge.read': ['owner', 'admin', 'manager', 'analyst', 'viewer'],
  'knowledge.write': ['owner', 'admin', 'manager'],
  'knowledge.publish': ['owner', 'admin', 'manager'],

  /*
   * `field_agent` can read codes because it can enrol, and an agent who may
   * enrol somebody but may not read the code they enrol them with cannot enrol
   * anybody. It still cannot mint one.
   */
  'codes.read': ['owner', 'admin', 'manager', 'analyst', 'field_agent'],
  'codes.write': ['owner', 'admin', 'manager'],

  /*
   * The member roster is individual records. `viewer` is excluded by the spec's
   * own words, and `field_agent` is excluded because browsing the community is
   * the specific thing that role does not do, it sees the people it enrolled,
   * which the server scopes, not the list.
   */
  'members.read': ['owner', 'admin', 'manager', 'analyst'],
  'members.enrol': ['owner', 'admin', 'manager', 'field_agent'],

  /*
   * The split that matters most in this table. The aggregate view carries no
   * identity and no words, so everyone who can read the project can read it.
   * The individual one carries what a person said, is refused without that
   * person's `share_with_org` grant, and writes an audit event on every open.
   * A viewer never gets it: "no individual records" is the whole definition of
   * the role.
   */
  'conversations.readAggregate': ['owner', 'admin', 'manager', 'analyst', 'viewer'],
  'conversations.readIndividual': ['owner', 'admin', 'manager', 'analyst'],

  'insights.read': ['owner', 'admin', 'manager', 'analyst', 'viewer'],
  'insights.review': ['owner', 'admin', 'manager'],

  /*
   * `cases.read` is browsing the organization's caseload. `cases.readAssigned`
   * is seeing your own, and it is the weaker of the two on purpose: it is what
   * lets a field agent open the work assigned to them without opening anybody
   * else's. Screens gate on the weaker one and let the server scope the rows,
   * because gating a caseload screen on `cases.read` locks an agent out of
   * their own cases.
   */
  'cases.read': ['owner', 'admin', 'manager', 'analyst'],
  'cases.readAssigned': ['owner', 'admin', 'manager', 'analyst', 'field_agent'],
  'cases.write': ['owner', 'admin', 'manager', 'field_agent'],
  'cases.assign': ['owner', 'admin', 'manager'],
  'cases.refer': ['owner', 'admin', 'manager'],

  /*
   * A broadcast is programme content rather than anybody's record, so a viewer
   * can read what was said in their organization's name. Sending is separate
   * from writing because a broadcast cannot be recalled once it is spoken.
   */
  'broadcasts.read': ['owner', 'admin', 'manager', 'analyst', 'viewer'],
  'broadcasts.write': ['owner', 'admin', 'manager'],
  'broadcasts.send': ['owner', 'admin', 'manager'],

  /* The same assigned-versus-all split as cases, for the same reason. */
  'tasks.read': ['owner', 'admin', 'manager', 'analyst'],
  'tasks.readAssigned': ['owner', 'admin', 'manager', 'analyst', 'field_agent'],
  'tasks.write': ['owner', 'admin', 'manager'],
  'tasks.report': ['owner', 'admin', 'manager', 'field_agent'],
  'tasks.review': ['owner', 'admin', 'manager'],

  'indicators.read': ['owner', 'admin', 'manager', 'analyst', 'viewer'],
  'indicators.write': ['owner', 'admin', 'manager'],

  /*
   * The audit log names staff and the people they looked at. A manager is
   * frequently the subject of it, which is the reason they are not a reader of
   * it; an analyst is included because reading everything is what the role is.
   */
  'audit.read': ['owner', 'admin', 'analyst'],

  'reports.export': ['owner', 'admin', 'manager', 'analyst'],
}

/**
 * Whether a role holds a permission.
 *
 * `null` is a real argument and not an oversight: it is what a route guard has
 * before anybody has signed in, and answering false is the only safe reading.
 */
export function can(role: OrgRole | null | undefined, permission: Permission): boolean {
  if (role === null || role === undefined) return false
  return GRANTS[permission].includes(role)
}

/** Every permission a role holds. For the account screen, and for tests. */
export function permissionsFor(role: OrgRole): Permission[] {
  return (Object.keys(GRANTS) as Permission[]).filter((permission) => can(role, permission))
}
